import { Button } from '@/components/ui/button';
import { AritoIcon } from '@/components/custom/arito/icon';
import { useAuth } from '@/contexts/auth-context';
import { cn } from '@/lib/utils';

interface BottomBarProps {
  mode: 'view' | 'edit' | 'add' | 'search';
  bottomBar?: React.ReactNode;
  onSubmit?: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  onClose: () => void;
  className?: string;
  loading?: boolean;
  submitTitle?: string;
}

interface ActionButtonProps {
  onClick?: () => void;
  icon: number;
  title: string;
  primary?: boolean;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'default' | 'outline';
}

export const ActionButton = ({
  onClick,
  icon,
  title,
  primary = true,
  type,
  variant = 'default',
  disabled
}: ActionButtonProps & { disabled?: boolean }) => {
  const handleClick = () => {
    if (type !== 'submit' && onClick) {
      onClick();
    }
  };

  return (
    <Button
      className={`flex items-center justify-center gap-2 px-4 py-2 capitalize ${
        primary ? 'bg-main text-white hover:bg-main/80' : 'border border-main text-main'
      }`}
      onClick={handleClick}
      type={type || 'button'}
      title={title}
      variant={variant === 'outline' ? 'outline' : undefined}
      disabled={disabled}
    >
      {icon != 0 && <AritoIcon icon={icon} className='mx-1' />}
      <p className='flex items-center justify-center text-sm font-bold'>{title}</p>
    </Button>
  );
};

export function BottomBar({
  mode,
  bottomBar,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
  onClose,
  className,
  loading: loadingProp,
  submitTitle = 'Lưu'
}: BottomBarProps) {
  const { loading } = useAuth();
  const isLoading = loadingProp || loading;
  const viewModeButtons = [
    { show: onAdd, onClick: onAdd, icon: 571, title: 'Thêm', primary: true },
    { show: onEdit, onClick: onEdit, icon: 9, title: 'Sửa' },
    { show: onDelete, onClick: onDelete, icon: 8, title: 'Xoá' },
    { show: onCopy, onClick: onCopy, icon: 11, title: 'Sao chép' }
  ];

  return (
    <div
      className={cn(
        `flex w-full justify-end gap-1 border-t border-gray-200 bg-[#f9fcfd] px-4 py-2 sm:px-4 sm:py-2`,
        className
      )}
    >
      {bottomBar || (
        <>
          {isLoading && (
            <div className='mr-auto mt-2 flex h-5 items-center justify-center'>
              <div className='size-8 animate-spin rounded-full border-b-[3px] border-t-[3px] border-main'></div>
            </div>
          )}
          {mode !== 'view' && (
            <ActionButton
              onClick={onSubmit}
              icon={884}
              title={submitTitle}
              primary
              type='submit'
              disabled={isLoading}
            />
          )}
          {mode === 'view' && (
            <>
              {viewModeButtons.map(
                button =>
                  button.show && (
                    <ActionButton
                      key={button.title}
                      onClick={button.onClick!}
                      icon={button.icon}
                      title={button.title}
                      primary={button.primary === true}
                      variant='outline'
                      disabled={isLoading}
                    />
                  )
              )}
            </>
          )}
          <ActionButton
            onClick={onClose}
            icon={885}
            variant='outline'
            primary={false}
            title='Huỷ'
            disabled={isLoading}
          />
        </>
      )}
    </div>
  );
}
